import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import searchIcon from '../../assets/icons/search.svg';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { INPUT_THEMES, INPUT_STYLE_TOKENS, type InputTheme } from './form/inputThemes';

type Academy = {
  id: string;
  name: string;
  city?: string;
  state?: string;
  country?: string;
  is_custom?: boolean;
};

interface AcademyAutocompleteProps {
  value: string | { id: string; name: string } | '';
  onChange: (value: string | { id: string; name: string }) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  inputGap?: string;
  inputStyle?: React.CSSProperties;
  /** Visual theme: 'dark' or 'light'. Defaults to 'dark'. */
  theme?: InputTheme;
}

export function AcademyAutocomplete({ value, onChange, placeholder, className, containerClassName, inputStyle, theme = 'dark' }: AcademyAutocompleteProps) {
  const tk = INPUT_THEMES[theme];
  const containerRef = useRef<HTMLDivElement>(null);
  const [filteredAcademies, setFilteredAcademies] = useState<Academy[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.name : '');

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  useEffect(() => {
    const fetchAcademies = async () => {
      if (!inputValue || inputValue.length < 1) {
        // Show all academies when input is empty or has less than 1 character
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('academies')
            .select('*')
            .order('name')
            .limit(20); // Limit to first 20 academies for performance
          if (error) {
            console.error('Error fetching academies:', error);
            setFilteredAcademies([]);
          } else {
            setFilteredAcademies(data || []);
          }
        } catch (error) {
          console.error('Error fetching academies:', error);
          setFilteredAcademies([]);
        } finally {
          setLoading(false);
        }
        return;
      }
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('academies')
          .select('*')
          .ilike('name', `%${inputValue}%`)
          .order('name');
        if (error) {
          console.error('Error fetching academies:', error);
          setFilteredAcademies([]);
        } else {
          setFilteredAcademies(data || []);
        }
      } catch (error) {
        console.error('Error fetching academies:', error);
        setFilteredAcademies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAcademies();
  }, [inputValue]);

  const handleSelect = (academy: Academy) => {
    onChange({ id: academy.id, name: academy.name });
    setOpen(false);
  };

  const handleCustomAcademy = async (academyName: string) => {
    try {
      
      // Create custom academy in the database
      const { data, error } = await supabase.rpc('create_or_get_custom_academy', {
        academy_name: academyName
      });
      
      
      
      if (error) {
        console.error('Error creating custom academy:', error);
        onChange({ id: null, name: academyName });
      } else {
        // Academy was created/found, use the name

        onChange({ id: data, name: academyName });
      }
    } catch (error) {
      console.error('Error creating custom academy:', error);
      onChange({ id: null, name: academyName });
    }
    setOpen(false);
  };

  // Calculate dropdown position for Portal rendering (escapes overflow clipping in modals).
  // position:fixed is viewport-relative, so we use getBoundingClientRect() directly
  // without adding window.scrollY/scrollX.
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  const handleInputChange = (inputValue: string) => {
    setInputValue(inputValue);
    setOpen(true);
  };

  return (
    <div ref={containerRef} className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none",
            theme === 'light' ? "brightness-0 opacity-40" : "opacity-40"
          )}
          style={{ zIndex: 2 }}
        />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          className={cn(
            'w-full border h-11 rounded-lg pl-12 pr-12 text-sm focus:outline-none transition-all duration-200 font-sans focus-visible:ring-1 focus-visible:ring-red-700',
            tk.input.split(' ').filter(cls => !cls.startsWith('h-') && !cls.startsWith('text-') && !cls.startsWith('font-')).join(' '),
            className
          )}
          style={{
            zIndex: 1,
            borderWidth: '1px',
            borderStyle: 'solid',
            fontSize: INPUT_STYLE_TOKENS[theme].fontSize,
            ...inputStyle
          }}
        />
        {inputValue && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              setInputValue('');
              onChange('');
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              theme === 'light'
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            data-testid="academy-autocomplete-clear-button"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      {open && dropdownRect && createPortal(
        <div
          className={['rounded-md shadow-lg max-h-60 overflow-auto', tk.dropdown].join(' ')}
          style={{
            position: 'fixed',
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 9999,
          }}
        >
          {loading ? (
            <div className={tk.helperText + ' p-4 text-center'}>
              Loading academies...
            </div>
          ) : filteredAcademies.length === 0 && inputValue.length >= 2 ? (
            <div className="py-1">
              <div
                className={cn('relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2', tk.dropdownItem)}
                onMouseDown={(e) => { e.preventDefault(); handleCustomAcademy(inputValue); }}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  <Check className="h-4 w-4 opacity-0" />
                </span>
                <span className={tk.dropdownItemText}>Add "{inputValue}"</span>
              </div>
            </div>
          ) : (
            <div className="py-1">
              {filteredAcademies.map((academy) => (
                <div
                  key={academy.id}
                  className={cn('relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2', tk.dropdownItem)}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(academy); }}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {inputValue === academy.name && <Check className={cn('h-4 w-4', tk.dropdownItemText)} />}
                  </span>
                  <div className="flex flex-col">
                    <span className={cn('text-sm font-medium', tk.dropdownItemText)}>{academy.name}</span>
                    <span className={cn('text-xs', tk.helperText)}>
                      {academy.city && academy.state ? `${academy.city}, ${academy.state}` : academy.city || academy.state || ''}
                      {academy.country && (academy.city || academy.state) ? `, ${academy.country}` : academy.country || ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}